import {useCallback, useEffect, useMemo, useState} from 'react';

import {i18n, type I18n} from './i18n';

interface I18nAPI {
  fileSize: typeof i18n.fileSize;
  t: typeof i18n.t;
  tArr: typeof i18n.tArr;
  tObj: typeof i18n.tObj;
}

/**
 * Hook to use i18n localization.
 *
 * It supports lazy loading of namespaces and live updates on language change.
 */
export const useI18n = (namespaces?: I18n.Namespace[]): I18nAPI => {
  const [forceUpdate, setForceUpdate] = useState(0);

  const loadNamespacesIfNeeded = useCallback(async () => {
    if (namespaces) {
      await i18n.loadNamespaces(namespaces);
    }
  }, [namespaces]);

  useEffect(() => {
    loadNamespacesIfNeeded();
  }, [loadNamespacesIfNeeded]);

  const forceUpdateCb = useCallback(() => {
    setForceUpdate(prev => prev + 1);
  }, []);

  useEffect(() => {
    i18n._instance.on('languageChanged', forceUpdateCb);

    return () => {
      i18n._instance.off('languageChanged', forceUpdateCb);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const waitForNamespaces = async () => {
      if (namespaces) {
        await i18n.awaitForNamespaces(namespaces);

        forceUpdateCb();
      }
    };

    waitForNamespaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = useMemo(() => {
    if (namespaces && !i18n.isNamespacesLoaded(namespaces)) {
      return () => '...';
    }

    return i18n.t.bind(i18n);
    // lng dependency is needed to trigger re-render on language change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceUpdate]);

  return {
    fileSize: i18n.fileSize.bind(i18n),
    t,
    tArr: i18n.tArr.bind(i18n),
    tObj: i18n.tObj.bind(i18n),
  };
};
